import { Alert, Button, Typography } from "@outschool/backpack";
import {
  AccountSectionNewQuery,
  BlockLearnerMutation,
  BlockLearnerMutationVariables,
} from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import { useMutation } from "@outschool/ui-apollo";
import { ErrorMessage } from "@outschool/ui-components-shared";
import { TextArea } from "@outschool/ui-legacy-component-library";
import React from "react";

import * as Enrollment from "../../../shared/Enrollment";
import * as Env from "../../../shared/Env";
import { AccountSectionQuery } from "../../queries/AccountSectionQuery";
import { blockLearnerMutation } from "../../queries/BlockLearnerMutations";
import Select from "../lib/Select";

enum BlockReason {
  AdultAttendedClass = "AdultAttendedClass",
}

export default function BlockLearnerModal({
  educatorUid,
  enrollment,
  handleBack,
  section,
}: {
  educatorUid: string;
  enrollment: NonNullable<
    AccountSectionNewQuery["section"]
  >["selectedEnrollment"];
  handleBack: () => void;
  section: AccountSectionNewQuery["section"];
}) {
  const [inProgress, setInProgress] = React.useState(false);
  const [reason, setReason] = React.useState<BlockReason>();
  const [note, setNote] = React.useState<string>();
  const [error, setError] = React.useState<string>();
  const { t } = useTranslation(
    "client\\components\\teacher\\BlockLearnerModal",
  );
  if (!enrollment || !enrollment?.learner) {
    throw new Error("No enrollment provided");
  }
  const learnerName = Enrollment.displayName(enrollment, enrollment?.learner);
  const [blockLearner] = useMutation<
    BlockLearnerMutation,
    BlockLearnerMutationVariables
  >(blockLearnerMutation, {
    refetchQueries: [
      {
        query: AccountSectionQuery,
        variables: {
          uid: section?.uid,
          learnerOrEnrollmentUid: enrollment?.learner?.uid,
          hasSelection: !!enrollment?.learner?.uid,
        },
      },
    ],
  });
  const handleSubmit = React.useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!reason) {
        return setError(t("Please select a reason."));
      }
      if (!note) {
        return setError(
          t("Please provide a basic description to help us investigate."),
        );
      }
      if (!enrollment?.learner?.uid) {
        throw new Error("No learner UID found.");
      }
      setInProgress(true);
      await blockLearner({
        variables: {
          educatorUid,
          learnerUid: enrollment.learner.uid,
          reason,
          note,
        },
      });
      setInProgress(false);
      handleBack();
    },
    [reason, note, enrollment, educatorUid, blockLearner, handleBack, t],
  );

  return (
    <>
      <Typography variant="h3" data-private>
        {t("Block {{learnerName}} from Outschool", { learnerName })}
      </Typography>
      <hr
        style={{
          marginTop: "1em",
          marginBottom: "1em",
        }}
      ></hr>
      <ErrorMessage value={error} showStatusPageLink={Env.IS_READ_ONLY_MODE} />
      <Alert severity="error">
        <Typography fontWeight="fontWeightBold">
          {t(
            "Blocking should only be done for serious safety concerns, not for misbehavior.",
          )}
        </Typography>
        <Typography>
          {t(
            "Remember, Blocking a learner means they lose access to all Outschool classes, including attending sessions, booking slots, or contacting teachers. We’ll also notify their family and the Trust & Safety team.",
          )}
        </Typography>
      </Alert>
      <Typography variant="h6" sx={{ marginY: "1em" }}>
        {t("Let us know why you blocked this learner.")}
      </Typography>
      <form onSubmit={handleSubmit}>
        <Select
          value={reason}
          onChange={(val: BlockReason) => setReason(val)}
          options={[
            {
              label: t("Adult joined as the learner"),
              value: BlockReason.AdultAttendedClass,
            },
          ]}
          placeholder={t("Select a reason")}
          sx={{ width: "100%" }}
        />
        <Typography variant="h6" sx={{ marginY: "1em" }}>
          {t("Tell us more")}
        </Typography>
        <TextArea
          rows={8}
          value={note}
          onChange={e => setNote(e.currentTarget.value)}
          placeholder={t("A short description of the incident.")}
        />
        <Button
          variant="contained"
          color="error"
          sx={{ marginTop: "1em" }}
          data-private
          type="submit"
          disabled={inProgress}
        >
          {inProgress
            ? t("Blocking...")
            : t("Block {{learnerName}}", { learnerName })}
        </Button>
      </form>
    </>
  );
}
